import React, { useEffect, useState } from 'react';
import { useAppSelector } from '@/store/hooks';

import clsx from 'clsx';
import IconArrowDown from '@/assets/icons/icon-arrow-down-01.svg?react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import IconSearchList from '@/assets/icons/icon-search-list.svg?react';
import { Textarea } from '@/components/ui/textarea';
import { TextField } from '@/components/inspector-fields/TextField/TextField';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { InfoTooltip } from '@/components/tooltips/InfoTooltip/InfoTooltip';
import type { SeoSettings } from '@builder4/shared/types/settings';
import { selectCurrentPageId } from '@/store/pages/selectors';
import { selectSeoSettings } from '@/store/settings/seo/selectors';
import { shallowEqual } from '@/utils/settingsHelpers';
import { useSettingsDebounceUpdate } from '@/hooks/useSettingsDebounceUpdate';

interface SettingsSeoProps {
  className: string;
  onClick?: () => void;
  isOpenCollapse: boolean;
}

const defaultSettings: SeoSettings = {
  seoPageTitle: '',
  seoPageDescription: '',
  seoPageKeywords: '',
  doNotIndexPage: false,
};

export const SettingsSeo: React.FC<SettingsSeoProps> = ({
  className = '',
  onClick,
  isOpenCollapse,
}) => {
  const seoSettingsRedux = useAppSelector(selectSeoSettings) ?? defaultSettings;
  const currentPageId = useAppSelector(selectCurrentPageId);
  const [isOpen] = useState(isOpenCollapse);
  const [seoSettings, setSeoSettings] = useState<SeoSettings>(seoSettingsRedux);

  const handleUpdateSeoSettings = <K extends keyof SeoSettings>(key: K, value: SeoSettings[K]) => {
    setSeoSettings((prevState) => ({ ...prevState, [key]: value }));
  };

  const handleClick = () => {
    if (onClick) onClick();
  };

  const { lastSavedValueRef } = useSettingsDebounceUpdate({
    value: seoSettings,
    bodyKey: 'seo_settings',
    currentPageId: currentPageId,
    reduxValue: seoSettingsRedux,
  });

  useEffect(() => {
    if (!seoSettingsRedux) return;

    setSeoSettings((prev) => {
      if (shallowEqual(prev, seoSettingsRedux)) return prev;
      if (lastSavedValueRef.current && shallowEqual(seoSettingsRedux, lastSavedValueRef.current)) {
        return prev;
      }
      return seoSettingsRedux;
    });
  }, [seoSettingsRedux, lastSavedValueRef]);

  return (
    <Collapsible defaultOpen={isOpen}>
      <CollapsibleTrigger
        onClick={handleClick}
        data-qa-selector="settings-seo-toggle"
        className={clsx(
          'group bg-neutral-0 w-full cursor-pointer border-t border-neutral-100 px-2 py-4',
          className,
        )}
      >
        <div className="flex w-full items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <IconSearchList className="h-4 w-4 text-neutral-900" />
            <span className="text-xs text-neutral-900">SEO</span>
          </div>
          <IconArrowDown className="h-4 w-4 text-neutral-500 transition-all group-aria-expanded:rotate-180" />
        </div>
      </CollapsibleTrigger>

      <CollapsibleContent className={clsx('bg-neutral-0 max-w-[232px] pb-3')}>
        <div className="flex flex-col gap-2">
          <div className="flex flex-col gap-2 p-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1">
                <p className="text-button-sm text-neutral-500">SEO Page Title</p>
                <InfoTooltip text="The headline that appears in search results and browser tabs." />
              </div>
            </div>
            <TextField
              className="w-full max-w-[216px]"
              classNameInputParent="w-full max-w-full"
              inputValue={seoSettings.seoPageTitle ?? ''}
              onChange={(e) => handleUpdateSeoSettings('seoPageTitle', e.target.value)}
              placeholderText="Title"
              state="default"
              text=""
              type="text"
            />
            <p className="text-[10px] font-medium text-neutral-400">
              Keep this under 70 characters.
            </p>
          </div>

          <div className="flex flex-col gap-2 p-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1">
                <p className="text-button-sm text-neutral-500">SEO Page Description</p>
                <InfoTooltip text="The preview text that shows with your title in search results. Explain what visitors will get on this page. A good description improves your click-through rate." />
              </div>
            </div>
            <Textarea
              className={'min-h-16 w-full resize-none'}
              placeholder={'Page description'}
              disabled={false}
              value={seoSettings.seoPageDescription ?? ''}
              onChange={(e) => handleUpdateSeoSettings('seoPageDescription', e.target.value)}
            />
            <p className="text-[10px] font-medium text-neutral-400">
              Keep this between 50 and 160 characters.
            </p>
          </div>

          <div className="flex flex-col gap-2 p-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1">
                <p className="text-button-sm text-neutral-500">SEO Page Keywords</p>
              </div>
            </div>
            <TextField
              className="w-full max-w-[216px]"
              classNameInputParent="w-full max-w-full"
              inputValue={seoSettings.seoPageKeywords ?? ''}
              onChange={(e) => handleUpdateSeoSettings('seoPageKeywords', e.target.value)}
              placeholderText="Add keywords"
              state="default"
              text=""
              type="text"
            />
            <p className="text-[10px] font-medium text-neutral-400">Separate keywords by commas.</p>
          </div>

          <div className="flex items-center gap-2 p-2">
            <Checkbox
              id="dontIndexPage"
              checked={seoSettings.doNotIndexPage ?? false}
              onCheckedChange={(checked) =>
                handleUpdateSeoSettings('doNotIndexPage', checked === true)
              }
            />
            <Label className="inline text-xs text-neutral-500" htmlFor="dontIndexPage">
              <span>I don’t want this page indexed.</span>
            </Label>
          </div>
          <div className="my-3 flex flex-col items-start p-2 text-[10px] font-medium text-neutral-400">
            <p>
              Want to learn more about these fields? <br />
              <a
                href="https://support.leadpages.com/hc/en-us/articles/115007582368-Tracking-and-third-party-analytics#search-engine-optimization-seo"
                className="text-indigo-500 underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                Click here
              </a>
            </p>
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};
